import React, { useMemo } from 'react';
import { tokenize } from './pace';

/**
 * Poem-style renderer: centered lines with individual word <span>s
 * for sync highlighting. Renders WITHOUT any per-line background —
 * the outer PRAYER_HIGHLIGHT_BOX_STYLE wrapper provides a single
 * highlight around the whole prayer.
 *
 * Extracted from WatchPrayer.jsx unchanged — same DOM output, same
 * className / style / data-testid / ref-callback semantics.
 */
const PoemText = ({ text, currentWordIndex, wordRefs }) => {
  const { tokens } = useMemo(() => tokenize(text), [text]);
  if (!text) return null;

  // Group tokens into paragraph → line arrays for rendering.
  const paragraphs = [];
  let currentPara = [[]];
  tokens.forEach((tok) => {
    if (tok.type === 'para') {
      paragraphs.push(currentPara);
      currentPara = [[]];
    } else if (tok.type === 'br') {
      currentPara.push([]);
    } else {
      currentPara[currentPara.length - 1].push(tok);
    }
  });
  paragraphs.push(currentPara);

  return (
    <div className="space-y-4 text-center max-w-2xl px-6 sm:px-10 lg:px-14 w-full">
      {paragraphs.map((para, pi) => (
        <p
          key={pi}
          className="leading-relaxed"
          style={{
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: 'clamp(1.4rem, 3.2vw, 2.1rem)',
            lineHeight: 1.65,
          }}
        >
          {para.map((line, li) => {
            // Blank lines still emit the <br /> so poem line-break
            // rhythm stays intact, but render nothing else — no
            // highlight pill.
            const hasVisibleText = line.some(
              (tok) => tok.type === 'word' && (tok.text || '').trim().length > 0
            );
            if (!hasVisibleText) {
              return (
                <React.Fragment key={li}>
                  {li > 0 && <br />}
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {line.map((tok, ti) => {
                  if (tok.type === 'space') return <span key={ti}> </span>;
                  // @handle tokens: rendered as neutral text with NO
                  // highlight state — they were assigned
                  // `wordIndex: null` by the tokenizer so the
                  // audio-driven index never lands on them. Keeps the
                  // on-screen text intact while matching what the
                  // narrator actually says.
                  if (tok.wordIndex === null || tok.wordIndex === undefined) {
                    return (
                      <span
                        key={ti}
                        data-word-index="handle"
                        style={{ color: 'rgba(255,255,255,0.65)' }}
                      >
                        {tok.text}
                      </span>
                    );
                  }
                  const isActive = tok.wordIndex === currentWordIndex;
                  const isPast = currentWordIndex >= 0 && tok.wordIndex < currentWordIndex;
                  // "Faux-bold" for the active word: we keep
                  // `font-weight: 400` on every word (so the rendered
                  // glyph width NEVER changes) and simulate the
                  // heavier stroke with a zero-offset text-shadow of
                  // the same amber color layered on top of the
                  // existing glow. This prevents the active word from
                  // reflowing the line — critical because Times New
                  // Roman's 400→700 metrics differ enough to force
                  // downstream words to wrap onto a new row.
                  const activeShadow = '0 0 0.5px #ffd166, 0 0 14px rgba(255, 209, 102, 0.9), 0 2px 4px rgba(0,0,0,0.75)';
                  return (
                    <span
                      key={ti}
                      ref={(el) => { if (el) wordRefs.current[tok.wordIndex] = el; }}
                      data-word-index={tok.wordIndex}
                      style={{
                        transition: 'color 120ms ease, text-shadow 120ms ease',
                        // White text throughout (rendered directly on
                        // the dimmed background image / gradient). The
                        // active word gets a warm amber tint with a
                        // soft glow so it pops without needing a
                        // container behind it; past words fade to a
                        // translucent white.
                        color: isActive ? '#ffd166' : (isPast ? 'rgba(255,255,255,0.7)' : 'rgb(255,255,255)'),
                        textShadow: isActive ? activeShadow : 'inherit',
                        // Keep font-weight constant → glyph width
                        // stays fixed → no karaoke-triggered line
                        // reflow.
                        fontWeight: 400,
                      }}
                    >
                      {tok.text}
                    </span>
                  );
                })}
              </React.Fragment>
            );
          })}
        </p>
      ))}
    </div>
  );
};

export default PoemText;
